import { createApi } from '@reduxjs/toolkit/query/react';
import { Member, MemberToList } from '../../components/Members/types';
import { baseQueryWithReauth } from './baseQuery';

export interface GetMembersParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface MembersResponse {
  payload: {
    content: MemberToList[];
    totalElements: number;
    totalPages: number;
    page: number;
  };
}

export interface MemberResponse {
  success: boolean;
  payload: Member;
}

export interface CreateMemberRequest {
  firstName: string;
  lastName: string;
  email: string;
  roleId: string;
}

export interface UpdateMemberRequest {
  id: number;
  firstName?: string;
  lastName?: string;
  roleId: string;
}

export const adminMembersService = createApi({
  reducerPath: 'adminMembersService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['MEMBERS'],
  endpoints: (builder) => ({
    getMembers: builder.query<MembersResponse['payload'], GetMembersParams>({
      query: (params) => ({
        url: '/v1/admin/members',
        params: {
          page: params.page || 0,
          limit: params.limit || 10,
          search: params.search || undefined,
        },
      }),
      transformResponse: (response: MembersResponse) => response.payload,
      providesTags: ['MEMBERS'],
    }),

    getMemberById: builder.query<Member, number>({
      query: (id) => ({
        url: `/v1/admin/members/${id}`,
      }),
      transformResponse: (response: { payload: Member }) => response.payload,
      providesTags: (_result, _error, id) => [{ type: 'MEMBERS', id }],
    }),

    createMember: builder.mutation<{ payload: string }, CreateMemberRequest>({
      query: (body) => ({
        url: '/v1/admin/members',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MEMBERS'],
    }),

    updateMember: builder.mutation<{ payload: string }, UpdateMemberRequest>({
      query: ({ id, ...body }) => ({
        url: `/v1/admin/members/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'MEMBERS',
        { type: 'MEMBERS', id },
      ],
    }),

    deleteMember: builder.mutation<void, number>({
      query: (id) => ({
        url: `/v1/admin/members/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MEMBERS'],
    }),
  }),
});

export const {
  useGetMembersQuery,
  useGetMemberByIdQuery,
  useCreateMemberMutation,
  useUpdateMemberMutation,
  useDeleteMemberMutation,
  useLazyGetMemberByIdQuery,
} = adminMembersService;
